"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import type { TeamsGalleryTeamOverlayHandle } from "@/components/ui/Cteamsgallery";
import {
  TeamModalContext,
  TeamModalHostBridgeContext,
  type TeamModalHostBridgeValue,
} from "./team-modal-context";
import {
  isNewSubjectOpenState,
  TEAM_MODAL_CLOSED_STATE,
  TeamModalMode,
  TeamModalStatus,
  TeamModalSubjectKind,
  type OpenExistingTeamModalInput,
  type OpenNewTeamModalInput,
  type TeamModalContextValue,
  type TeamModalModeState,
  type TeamModalState,
} from "./team-modal-types";

function toModeState(input: OpenExistingTeamModalInput): TeamModalModeState {
  if (input.mode === TeamModalMode.Edit) {
    return { mode: TeamModalMode.Edit, editIntent: input.editIntent };
  }
  return { mode: input.mode };
}

/**
 * The app-level owner of the ONE team modal. Holds the canonical state and
 * exposes `openExisting`/`back`/`close`; the actual overlay renders in
 * `TeamModalHost`, mounted separately (below the tweaks owner) and wired back
 * here through the host bridge. In-modal mode transitions (deploy entered or
 * left, editor exited) are REPORTED by the overlay, so the tracked mode stays
 * a mirror of what is on screen rather than a second machine.
 */
export function TeamModalProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const [state, setState] = useState<TeamModalState>(TEAM_MODAL_CLOSED_STATE);
  const [openGeneration, setOpenGeneration] = useState(0);
  const overlayHandleRef = useRef<TeamsGalleryTeamOverlayHandle | null>(null);
  const editorWasActiveRef = useRef(false);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  });

  // A route transition under an open modal means the user left the surface
  // the modal belonged to — the ruling case is deploy/quick-start landing on
  // the session page, which used to leave this host open behind every session
  // (frozen preflight, a stale modal-stack entry duplicating the history
  // step, and home's URL sync re-stamping ?viewTeam over a later return).
  // The state settles Closed immediately: no guarded close (a discard
  // confirm has no place mid-navigation), no collapse flight, no focus
  // return. Same-route URL rewrites (?viewTeam sync, missionPrefill strip)
  // keep their pathname, so they never trip this.
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    if (pathnameRef.current === pathname) return;
    pathnameRef.current = pathname;
    setState((current) =>
      current.status === TeamModalStatus.Open
        ? TEAM_MODAL_CLOSED_STATE
        : current,
    );
  }, [pathname]);

  // Opening another subject while the modal is already open is a §1.4.1
  // abandonment boundary: the request funnels through the live overlay's
  // guarded close and proceeds ONLY once the guards let it through (a dirty
  // editor or deploy draft raises its discard confirm; cancel drops the new
  // open on the floor — nothing is stashed, so no stale replay can fire
  // later). With no overlay mounted there is nothing dirty to guard.
  const openThroughGuards = useCallback((applyOpen: () => void): void => {
    const handle = overlayHandleRef.current;
    if (stateRef.current.status === TeamModalStatus.Open && handle != null) {
      handle.requestClose(applyOpen);
      return;
    }
    applyOpen();
  }, []);

  const openExisting = useCallback(
    (input: OpenExistingTeamModalInput): void => {
      openThroughGuards(() => {
        editorWasActiveRef.current = false;
        setState({
          status: TeamModalStatus.Open,
          subject: {
            kind: TeamModalSubjectKind.Existing,
            teamId: input.teamId,
          },
          modeState: toModeState(input),
          origin: input.origin,
        });
        // A fresh generation remounts the overlay, so a second open while one
        // is already up re-enters through the full mount path (fresh morph,
        // fresh initial mode) instead of mutating a live overlay from outside.
        setOpenGeneration((generation) => generation + 1);
      });
    },
    [openThroughGuards],
  );

  const openNew = useCallback(
    (input: OpenNewTeamModalInput): void => {
      openThroughGuards(() => {
        editorWasActiveRef.current = false;
        setState({
          status: TeamModalStatus.Open,
          subject: { kind: TeamModalSubjectKind.New },
          modeState: { mode: TeamModalMode.Edit },
          origin: input.origin,
        });
        setOpenGeneration((generation) => generation + 1);
      });
    },
    [openThroughGuards],
  );

  const back = useCallback((): void => {
    // The overlay owns the in-modal machinery, so back is delegated to its
    // guarded paths: deploy → that team's regular state in place; otherwise
    // the guarded close (a dirty edit raises its discard confirm). With no
    // overlay mounted there is nothing to go back from.
    overlayHandleRef.current?.requestBack();
  }, []);

  const close = useCallback((afterClose?: () => void): void => {
    const handle = overlayHandleRef.current;
    if (handle != null) {
      // Route through the overlay: dirty guards intercept, and the collapse
      // flight runs before the state settles Closed via onOverlayClosed. The
      // overlay runs `afterClose` only once the guards accept — a cancelled
      // discard drops it, so nothing the caller chained happens either.
      handle.requestClose(afterClose);
      return;
    }
    // Not mounted yet (open just requested, chunk still loading) — nothing to
    // guard or animate, so the close is accepted immediately.
    setState(TEAM_MODAL_CLOSED_STATE);
    afterClose?.();
  }, []);

  const onOverlayClosed = useCallback((): void => {
    const closedFrom = stateRef.current;
    setState(TEAM_MODAL_CLOSED_STATE);
    if (closedFrom.status === TeamModalStatus.Open) {
      closedFrom.origin.returnFocus?.focus();
    }
  }, []);

  const onPreflightActiveChange = useCallback((active: boolean): void => {
    setState((current) => {
      if (current.status !== TeamModalStatus.Open) return current;
      // A New subject has no deploy (or present) reading to move to — the
      // overlay never mounts a preflight for it, and its mount-time `false`
      // report must leave the creation state untouched.
      if (isNewSubjectOpenState(current)) return current;
      if (active) {
        if (current.modeState.mode === TeamModalMode.Deploy) return current;
        return { ...current, modeState: { mode: TeamModalMode.Deploy } };
      }
      if (current.modeState.mode !== TeamModalMode.Deploy) return current;
      return { ...current, modeState: { mode: TeamModalMode.Present } };
    });
  }, []);

  /** The overlay swapped itself onto another team IN PLACE and says so: a
   *  create landing, a save-copy landing, the linked-copy "Show", a
   *  saved-copies pick, or the deploy preflight's show-details. Every one of
   *  them keeps the SAME overlay — so the subject swaps on the same open
   *  generation (no remount, no re-morph, no second modal) and the tracked
   *  subject never names a team other than the one on screen. The retarget
   *  lands on that team's regular reading, which is the state the retargeted
   *  surface shows; the in-modal deploy/edit reports move it from there. */
  const onSubjectRetargeted = useCallback(
    ({ teamId }: { teamId: string }): void => {
      setState((current) => {
        if (current.status !== TeamModalStatus.Open) return current;
        if (
          current.subject.kind === TeamModalSubjectKind.Existing &&
          current.subject.teamId === teamId &&
          current.modeState.mode === TeamModalMode.Present
        ) {
          return current;
        }
        return {
          status: TeamModalStatus.Open,
          subject: { kind: TeamModalSubjectKind.Existing, teamId },
          modeState: { mode: TeamModalMode.Present },
          origin: current.origin,
        };
      });
    },
    [],
  );

  /** Drop an existing subject's tracked Edit mode to its regular reading. A
   *  New subject has no record to present, so its state is left alone. */
  const trackPresentMode = useCallback((): void => {
    setState((current) => {
      if (current.status !== TeamModalStatus.Open) return current;
      if (isNewSubjectOpenState(current)) return current;
      if (current.modeState.mode !== TeamModalMode.Edit) return current;
      return { ...current, modeState: { mode: TeamModalMode.Present } };
    });
  }, []);

  // The edit-mode open resolved to the ratified view fallback: `mode=edit` on
  // a team the viewer cannot edit, or `mode=copy` whose sign-in gate the viewer
  // dismissed instead of signing in. The surface is showing the regular state,
  // so the tracked mode follows it — otherwise the controller and the screen
  // disagree about which of the three states the one modal is in.
  const onEditIntentFallback = useCallback((): void => {
    trackPresentMode();
  }, [trackPresentMode]);

  const onEditorActiveChange = useCallback((active: boolean): void => {
    // Only a true→false TRANSITION is a downgrade: an entry-mode edit the
    // user leaves lands the state (and the URL grammar) back on the regular
    // reading. The surface reports `false` once at mount — before an
    // entry-mode editor has had a chance to open — and that report must not
    // cancel the edit entry. In-modal edit entries stay overlay-internal for
    // now: nothing consumes an upgraded mode, and the report carries no
    // intent to record.
    const editorWasActive = editorWasActiveRef.current;
    editorWasActiveRef.current = active;
    if (active || !editorWasActive) return;
    // A New subject's editor never downgrades to a present reading (there is
    // no record to present): leaving its edit either closes the modal or
    // rides the create's subject swap, both reported on their own channels.
    trackPresentMode();
  }, [trackPresentMode]);

  const contextValue = useMemo<TeamModalContextValue>(
    () => ({ state, openExisting, openNew, back, close }),
    [state, openExisting, openNew, back, close],
  );

  const openState = state.status === TeamModalStatus.Open ? state : null;
  const bridgeValue = useMemo<TeamModalHostBridgeValue>(
    () => ({
      openState,
      openGeneration,
      overlayHandleRef,
      onOverlayClosed,
      onPreflightActiveChange,
      onEditorActiveChange,
      onEditIntentFallback,
      onSubjectRetargeted,
    }),
    [
      openState,
      openGeneration,
      onOverlayClosed,
      onPreflightActiveChange,
      onEditorActiveChange,
      onEditIntentFallback,
      onSubjectRetargeted,
    ],
  );

  return (
    <TeamModalContext.Provider value={contextValue}>
      <TeamModalHostBridgeContext.Provider value={bridgeValue}>
        {children}
      </TeamModalHostBridgeContext.Provider>
    </TeamModalContext.Provider>
  );
}
